import React from 'react';
import type { ReactElement } from 'react';

import type { HeadingAnchorLinksProps } from '@atlaskit/renderer/types';
import type { ReactSerializerInit } from '@atlaskit/renderer/react';
import ReactSerializer from '@atlaskit/renderer/react';

import { NodeRenderer } from './NodeRenderer.tsx';
import { NullRenderer } from './NullRenderer.tsx';
import { RootRenderer } from './RootRenderer.tsx';

export class ProgressiveReactSerializer extends ReactSerializer {
	/**
	 * The index of the batch in which {@link activeHeadingId} is (to be) rendered.
	 */
	private activeHeadingBatchIndex: number | undefined = undefined;
	private activeHeadingId: ReturnType<typeof getActiveHeadingId>;

	private batchSize: number;

	/**
	 * The first invalid index (after a sequence of valid indices) of a node to batch render.
	 * After `serializeFragment` completes for the root node, this will be the number of nodes to batch render.
	 */
	private maxRenderIndex: number = 0;

	private onlySerializeFirstBatch: boolean;
	private onBatchRendered?: (done: boolean) => void;

	constructor(
		init: ReactSerializerInit,
		batchSize: number,
		onlySerializeFirstBatch: boolean = false,
		onBatchRendered?: (done: boolean) => void,
	) {
		const activeHeadingId = getActiveHeadingId(init.allowHeadingAnchorLinks);

		// If rendering from a linked anchor, the heading IDs are necessary to start the first render from the linked anchor:
		if (activeHeadingId && init.disableHeadingIDs) {
			init = {
				...init,
				disableHeadingIDs: false,
			};
		}

		super(init);

		this.activeHeadingId = activeHeadingId;
		this.batchSize = batchSize;
		this.onlySerializeFirstBatch = onlySerializeFirstBatch;
		this.onBatchRendered = onBatchRendered;

		this.maybeCountTextNodes();
	}

	/**
	 * Like {@link ReactSerializer.resetState}. Named differently because a class cannot have a separate declaration of a private property of its base class.
	 */
	private resetProgressiveState() {
		this.activeHeadingBatchIndex = undefined;

		this.maxRenderIndex = 0;
	}

	/**
	 * @override
	 */
	serializeFragment(
		fragment: SerializeFragmentParameters[0],
		props?: SerializeFragmentParameters[1],
		_target?: SerializeFragmentParameters[2],
		key?: SerializeFragmentParameters[3],
		parentInfo?: SerializeFragmentParameters[4],
	): JSX.Element | null {
		const isRoot = key === undefined; // Like ReactSerializer.serializeFragment

		if (isRoot) {
			this.resetProgressiveState();
		}

		const renderIndex = this.maxRenderIndex++;

		const shouldRenderNodeProgressively = !shouldSkipForWorkaround(fragment, props, parentInfo);

		const shouldSkipNodeSerialization =
			shouldRenderNodeProgressively &&
			this.onlySerializeFirstBatch &&
			// Skip serialization of anything beyond the first batch:
			renderIndex >= this.batchSize &&
			// This optimization cannot be used when rendering from a linked anchor that's not in the first batch:
			(!this.activeHeadingId || this.activeHeadingBatchIndex === 0);

		if (shouldSkipNodeSerialization) {
			// Returning a component, as opposed to null, is important to avoid breaking existing logic
			// within ADF node components that assume their children are React elements. NullRenderer
			// renders nothing, but is still a valid React element.
			return <NullRenderer />;
		}

		const nodeElement = super.serializeFragment.apply(
			this,
			arguments as unknown as SerializeFragmentParameters,
		);

		if (!nodeElement) {
			return nodeElement;
		}

		if (this.activeHeadingId && this.activeHeadingBatchIndex === undefined) {
			this.maybeSetActiveHeadingBatchIndex(props, renderIndex);
		}

		if (!shouldRenderNodeProgressively) {
			return nodeElement;
		}

		return this.renderNodeProgressively(nodeElement, isRoot, key, renderIndex);
	}

	/**
	 * {@link RootRenderer} builds render batches based on {@link maxRenderIndex}, but {@link serializeFragment} does not visit `textWrapper` nodes and, therefore, does not count `text` nodes.
	 * Since `text` nodes are used by most pages and the average number of `text` nodes appears to increase as Time to Visually Complete (TTVC) increases (e.g. https://docs.google.com/spreadsheets/d/1SlRKS3BdASJFABu75t4lb7I57zagPtWUBnRZ88Vfw0E),
	 * counting `text` modes may effectively produce smaller batches which in turm may lead to improved TTVC90.
	 */
	private maybeCountTextNodes() {
		// TODO Make `serializeTextWrapper` protected in `ReactSerializer` to improve the reliability and maintainability of this solution.
		const superSerializeTextWrapper = this[SERIALIZE_TEXT_WRAPPER_PNAME];
		if (typeof superSerializeTextWrapper === 'function') {
			this[SERIALIZE_TEXT_WRAPPER_PNAME] = function serializeTextWrapper(
				content: SerializeTextWrapperParameters[0],
			) {
				const nodeElement = superSerializeTextWrapper.apply(
					this,
					arguments as unknown as SerializeTextWrapperParameters,
				);

				if (Array.isArray(content)) {
					this.maxRenderIndex += content.length;
				}

				return nodeElement;
			};
		}
	}

	private maybeSetActiveHeadingBatchIndex(
		props: SerializeFragmentParameters[1],
		renderIndex: number,
	) {
		if (props?.nodeType === 'heading') {
			const { headingId } = props;
			if (headingId && headingId === this.activeHeadingId) {
				this.activeHeadingBatchIndex = Math.floor(renderIndex / this.batchSize);
			}
		}
	}

	private renderNodeProgressively(
		nodeElement: ReactElement,
		isRoot: boolean,
		key: string | undefined,
		renderIndex: number,
	) {
		if (isRoot) {
			return (
				<RootRenderer
					batchSize={this.batchSize}
					maxRenderIndex={this.maxRenderIndex}
					onBatchRendered={this.onBatchRendered}
					targetBatchIndex={this.activeHeadingBatchIndex}
				>
					{nodeElement}
				</RootRenderer>
			);
		} else {
			return (
				<NodeRenderer key={key} renderIndex={renderIndex}>
					{nodeElement}
				</NodeRenderer>
			);
		}
	}
}

function getActiveHeadingId(allowHeadingAnchorLinks?: HeadingAnchorLinksProps) {
	if (allowHeadingAnchorLinks && typeof allowHeadingAnchorLinks === 'object') {
		return allowHeadingAnchorLinks.activeHeadingId;
	}
}

function isHeaderTableRow(
	fragment: SerializeFragmentParameters[0],
	props: SerializeFragmentParameters[1],
	parentInfo: SerializeFragmentParameters[4],
) {
	try {
		if (props?.nodeType === 'tableRow' && parentInfo) {
			const parentPath = parentInfo?.path;
			if (parentPath?.length > 1) {
				const tableNode = parentPath[parentPath.length - 2];
				if (
					tableNode?.type?.name === 'table' &&
					parentPath[parentPath.length - 1]?.type?.name === 'tableRow' &&
					tableNode.maybeChild(0)?.content === fragment
				) {
					return true;
				}
			}
		}
	} catch (e) {
		if (process.env.NODE_ENV !== 'production') {
			throw e;
		}
	}
	return false;
}

type SerializeFragmentParameters = Parameters<ReactSerializer['serializeFragment']>;

/**
 * The name of the {@link ReactSerializer} method/property which serializes `textWrapper` and, therefore, `text` nodes.
 */
const SERIALIZE_TEXT_WRAPPER_PNAME = 'serializeTextWrapper';
type SerializeTextWrapperParameters = Parameters<
	ReactSerializer[typeof SERIALIZE_TEXT_WRAPPER_PNAME]
>;

/**
 * Cases in which node elements created by the super `ReactSerializer` should not be preogressively rendered
 * in order to avoid breakages of functionality.
 */
function shouldSkipForWorkaround(
	fragment: SerializeFragmentParameters[0],
	props: SerializeFragmentParameters[1],
	parentInfo: SerializeFragmentParameters[4],
) {
	return (
		// Some node components rely on the type of children. Do not wrap such children.
		// For example, there is an expectation in the table component(s) in `isHeaderRowEnabled` of `TableHeader`.
		// There is an expectation in the table component(s) in `isHeaderRowEnabled` about the children of the first row.
		props?.nodeType === 'tableHeader' || isHeaderTableRow(fragment, props, parentInfo)
	);
}
